<template>
    <v-card class="pa-3 container__card">
      <v-card-title>Log Phone Call</v-card-title>
      <v-card-text>
        <!-- Need to capture Phone Number, Subject, Outcome (Dropdown), Notes-->
        <v-form ref="form" v-model="valid" lazy-validation>
          <number-field
            :options="{ type: `phone` }"
            label="Phone Number"
            v-model="phoneNumber"
            :rules="[rules.required]"
            dense
          />
          <v-row class="mt-4">
            <v-col :cols="entity && entity.pgId ? 8 : 12">
              <v-autocomplete
                v-model="entity"
                :items="entityItems"
                :loading="loading || loadingCompanies"
                :search-input.sync="companySearch"
                label="Customer"
                item-text="entityid"
                item-value="id"
                :rules="[rules.required]"
                placeholder="Search by name or start typing..."
                return-object
                dense
                clearable
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-title>
                      {{ companySearch ? `No customers found matching "${companySearch}"` : 'Start typing to search customers' }}
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="4" v-if="entity && entity.pgId">
              <v-btn color="secondary" class="mt-2" text @click="switchToCompany(entity.pgId, entity.entityid)">Select Company</v-btn>
            </v-col>
          </v-row>
          <v-text-field v-model="subject" label="Subject" type="text" placeholder="Subject" dense />
          <v-select
            label="Outcome"
            v-model="outcome"
            :items="outcomes"
            item-text="name"
            item-value="id"
            :rules="[rules.required]"
            dense
          />
          <v-textarea v-model="notes" label="Notes" placeholder="Notes" dense />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn text @click="closeAndReset">Close</v-btn>
        <v-btn color="primary" @click="submit" :loading="submitting">Log Call</v-btn>
      </v-card-actions>
    </v-card>
</template>

<script>
import NumberField from '@/components/NumberField.vue'
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import {mapGetters} from "vuex";
import request from '@/util/request'

export default {
  components: {NumberField},
  props: {
    phoneNumber: String
  },
  watch: {
    phoneNumber() {
      this.searchByPhone()
    },
    companySearch(val) {
      // Don't search if value is empty or if we already have a matching entity selected
      if (!val || (this.entity && this.entity.entityid === val)) return
      this.searchCompaniesDebounced(val)
    }
  },
  async mounted() {
    if (this.phoneNumber) await this.searchByPhone()
  },
  methods: {
    closeAndReset() {
      this.resetForm()
      this.$emit('close')
    },
    resetForm() {
      // Kill all pending timers
      clearTimeout(this.timerId)
      clearTimeout(this.companyTimerId)

      // Invalidate all in-flight API requests
      this.searchVersion++

      if (this.$refs.form) {
        this.$refs.form.reset()
      }
      this.entity = null
      this.entityItems = []
      this.subject = null
      this.notes = null
      this.outcome = null
      this.companySearch = null
      this.formattedPhoneNumber = null

      // Force stop all spinners
      this.loading = false
      this.loadingCompanies = false
    },
    async switchToCompany(id, name) {
      this.$store.dispatch(`fetchAndSelectCompany`, id).then(async () => {
        await this.$router.push(`/dashboard`)
        window._VMA.$emit('SHOW_SNACKBAR', {
          text: `${name}/${id} is now your active company`,
          color: 'success',
        })
      })
    },
    async submit() {
      const data = {
        phoneNumber: this.formattedPhoneNumber,
        entity: this.entity,
        subject: this.subject,
        outcome: this.outcome,
        notes: this.notes,
        employeeId: this.getUserProfile.netsuite_internalId
      }
      // validate form and if valid, submit using logCall
      if (this.$refs.form.validate()) {
        this.submitting = true
        try {
          this.response = await this.$store.dispatch('logCall', data)
          window._VMA.$emit('SHOW_SNACKBAR', {
            text: `Your phone call with ${this.entity.entityid} has been logged in Netsuite.`,
            color: 'success',
          })
          this.resetForm()
        } catch (e) {
          window._VMA.$emit('SHOW_SNACKBAR', {
            text: e.message,
            color: 'error',
          })
        } finally {
          this.submitting = false
        }
      }
    },
    formatPhoneNumber(phone, defaultCountry = 'US') {
      const phoneNumber = parsePhoneNumberFromString(phone, defaultCountry);
      return phoneNumber ? phoneNumber.format('E.164') : null;
    },
    async searchByPhone() {
      // ALWAYS clear old timers first to prevent stale callbacks
      clearTimeout(this.companyTimerId)
      clearTimeout(this.timerId)
      this.loading = false
      this.loadingCompanies = false

      // ALWAYS increment version to invalidate any in-flight requests
      const version = ++this.searchVersion

      this.formattedPhoneNumber = this.formatPhoneNumber(this.phoneNumber)
      if (!this.formattedPhoneNumber || this.formattedPhoneNumber.length <= 7) return

      this.loading = true

      this.timerId = setTimeout(async () => {
        try {
          const results = await this.$store.dispatch('searchByPhone', {phone: this.formattedPhoneNumber})
          if (this.searchVersion === version) {
            this.entityItems = results
            this.entity = results[0]
          }
        } finally {
          if (this.searchVersion === version) {
            this.loading = false
          }
        }
      }, 500)
    },
    searchCompaniesDebounced(query) {
      clearTimeout(this.timerId)
      clearTimeout(this.companyTimerId)
      this.loading = false

      const version = ++this.searchVersion
      this.companyTimerId = setTimeout(async () => {
        await this.searchCompanies(query, version)
      }, 300)
    },
    async searchCompanies(query, version) {
      this.loadingCompanies = true
      try {
        const data = await request({
          url: '/companies/',
          params: { query: { search: query, active: true }, limit: 10, offset: 0 },
          method: 'get',
        })
        if (this.searchVersion === version) {
          this.entityItems = data.data.rows.map(company => ({
            ...company,
            entityid: company.name,
            pgId: company.id,
            id: company.netsuite_internalId
          }))
        }
      } catch (error) {
        if (this.searchVersion === version) {
          window._VMA.$emit('SHOW_SNACKBAR', {
            text: 'Failed to search companies',
            color: 'error',
          })
        }
      } finally {
        if (this.searchVersion === version) {
          this.loadingCompanies = false
        }
      }
    }
  },
  computed: {
    ...mapGetters(['getUserProfile', 'getSelectedCompany']),
  },
  data() {
    return {
      rules: {
        required: (value) => !!value || 'This field is required',
      },
      searchVersion: 0,
      timerId: null,
      companyTimerId: null,
      response: {},
      loading: false,
      loadingCompanies: false,
      submitting: false,
      valid: false,
      entity: null,
      subject: null,
      formattedPhoneNumber: null,
      notes: null,
      outcome: null,
      companySearch: null,
      entityItems: [],
      outcomes: [
        {
          "id": "14",
          "name": "2021 Silver Eagle Calls "
        },
        {
          "id": "8",
          "name": "Accounting: Account Balance Inquiry"
        },
        {
          "id": "9",
          "name": "Accounting: Compliance"
        },
        {
          "id": "10",
          "name": "Accounting: Credit Inquiry"
        },
        {
          "id": "11",
          "name": "Accounting: Payment Status"
        },
        {
          "id": "12",
          "name": "Accounting: Reference Check"
        },
        {
          "id": "13",
          "name": "Accounting: Wire/ACH Maintenance"
        },
        {
          "id": "2",
          "name": "Checking availability (explain in notes)"
        },
        {
          "id": "3",
          "name": "Checking order status (explain in notes)"
        },
        {
          "id": "4",
          "name": "Customer outbound (explain in notes)"
        },
        {
          "id": "15",
          "name": "New Account Followup"
        },
        {
          "id": "7",
          "name": "Other Customer Service (explain in notes)"
        },
        {
          "id": "6",
          "name": "Purchase trade executed "
        },
        {
          "id": "1",
          "name": "Quote (explain in notes)"
        },
        {
          "id": "5",
          "name": "Sale trade executed "
        }
      ]
    }
  },
}
</script>

<style lang="sass" scoped>
.container
  width: 600px
  margin: 0 auto

.container__card
  width: 600px
</style>